import {gql} from 'graphql-request';
import {useCallback} from 'react';
import {MyCoupon, MyCouponHistory, UseCouponResult} from '@/interface';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {getActivatedCouponHistory} from '@/utils';
import {useGqlRequest} from './request';

export const MY_COUPONS_QUERY_KEY = 'coupon/my-coupons';

const getQueryKey = (userId?: string) => [MY_COUPONS_QUERY_KEY, userId ?? 'guest'];

/**
 * 보유 쿠폰 목록.
 * 사용 가능한(보유 상태) 쿠폰만 내려오며, 전화번호로 지급된 쿠폰은 이 시점에 계정에 자동 연결된다.
 */
export const useMyCoupons = (userId?: string) => {
  const request = useGqlRequest({
    query: MY_COUPONS,
  });

  const queryFn = (): Promise<{result?: MyCoupon[]}> => {
    return request({});
  };

  const select = (data?: {result?: MyCoupon[]}) => data?.result;

  return useQuery({
    queryFn,
    enabled: !!userId?.length,
    queryKey: getQueryKey(userId),
    select,
  });
};

export const useRefetchMyCoupons = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({queryKey: [MY_COUPONS_QUERY_KEY]});
  }, [queryClient]);
};

/** 등록 성공한 쿠폰을 목록 최상단에 즉시 추가한다 (목록 재조회 없이 화면 반영) */
export const usePrependMyCoupon = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (userId: string | undefined, coupon: MyCoupon) => {
      queryClient.setQueryData(
        getQueryKey(userId),
        (prev?: {result?: MyCoupon[]}) => {
          const list = prev?.result ?? [];

          if (list.some((i) => i.id === coupon.id)) {
            return prev;
          }

          return {...prev, result: [coupon, ...list]};
        }
      );
    },
    [queryClient]
  );
};

/**
 * 쿠폰 이용 이력 (조회 전용).
 * 사용을 시작한 적이 있는 쿠폰만 화면에 표시한다 (마이페이지 `쿠폰 이용 이력`).
 */
export const useMyCouponHistory = (userId?: string) => {
  const request = useGqlRequest({
    query: MY_COUPON_HISTORY,
  });

  const queryFn = (): Promise<{result?: MyCouponHistory[]}> => {
    return request({});
  };

  const select = (data?: {result?: MyCouponHistory[]}) =>
    getActivatedCouponHistory(data?.result);

  return useQuery({
    queryFn,
    enabled: !!userId?.length,
    queryKey: ['coupon/my-coupon-history', userId],
    select,
  });
};

/** 공유 코드 등록. 보유 상태로만 추가되며 사용은 useCoupon 으로 따로 호출한다. */
export const useRegisterCouponCode = () => {
  const request = useGqlRequest({
    query: REGISTER_COUPON_CODE,
  });

  const mutationFn = (record: {
    code: string;
  }): Promise<{
    result?: MyCoupon;
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
  });
};

/** 보유 쿠폰 사용하기. 성공 시 tier/이용 기간이 부여된다. */
export const useUseCoupon = () => {
  const request = useGqlRequest({
    query: USE_COUPON,
  });

  const mutationFn = (record: {
    issueId: string;
  }): Promise<{
    result?: UseCouponResult;
  }> => {
    return request(record);
  };

  return useMutation({
    mutationFn,
  });
};

const MY_COUPON_RESULT = `
  id
  name
  sequence
  issueRoute
  applyType
  registerDeadline
  durationDays
  useBeganAt
  useEndedAt
  usableNow
  blockedReason
`;

const MY_COUPONS = gql`
  query MyCoupons {
    result: myCoupons {
      ${MY_COUPON_RESULT}
    }
  }
`;

const REGISTER_COUPON_CODE = gql`
  mutation RegisterCouponCode($code: String!) {
    result: registerCouponCode(code: $code) {
      ${MY_COUPON_RESULT}
    }
  }
`;

const MY_COUPON_HISTORY = gql`
  query MyCouponHistory {
    result: myCouponHistory {
      name
      sequence
      status
      useBeganAt
      useEndedAt
      couponBeganAt
      couponEndedAt
      releasedAt
      registerDeadline
      applyType
      durationDays
    }
  }
`;

const USE_COUPON = gql`
  mutation UseCoupon($issueId: ID!) {
    result: useCoupon(issueId: $issueId) {
      tier
      useBeganAt
      useEndedAt
    }
  }
`;
